import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Brain, Sparkles, ChevronRight, MapPin, Star, Clock, MessageSquare, History, Banknote, CalendarCheck, FileSignature, ClipboardCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import SessionsList from "../components/SessionsList";
import MonthlyReportCard from "../components/MonthlyReportCard";
import { useAuth, API } from "../context/AuthContext";
import { useFmtXof, useDateFmt } from "../lib/i18nFmt";

const statusStyle = (s) => {
  if (s === "PAID") return "bg-[#F0FDF4] text-[#15803D] border border-[#16A34A]/40";
  if (s === "PENDING_PAYMENT") return "bg-yellow-50 text-yellow-700 border border-yellow-200";
  if (s === "PAYMENT_FAILED") return "bg-red-50 text-red-700 border border-red-200";
  return "bg-gray-50 text-gray-700 border border-gray-200";
};

export default function ParentDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const fmt = useFmtXof();
  const dfmt = useDateFmt();
  const [history, setHistory] = useState([]);
  const [sent, setSent] = useState([]);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    axios.get(`${API}/match/history`).then(({ data }) => setHistory(data.items || [])).catch(() => {});
    axios.get(`${API}/contact-request/sent`).then(({ data }) => setSent(data.items || [])).catch(() => {});
    axios.get(`${API}/bookings`).then(({ data }) => setBookings(data.items || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFCFB]" data-testid="parent-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="rounded-3xl bg-white border border-[#E5EBE7] p-8 sm:p-10 relative overflow-hidden">
          <span className="geo-shape bg-[#16A34A]/15 h-64 w-64 -top-20 -right-20" aria-hidden />
          <div className="relative">
            <span className="pill"><Sparkles className="h-3.5 w-3.5" /> {t("parentDash.pill")}</span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-[#0A2518]">
              {t("parentDash.greeting", { name: user?.name?.split(" ")[0] || "" })}
            </h1>
            <p className="mt-3 text-[#4B5563] max-w-2xl">{t("parentDash.intro")}</p>
            {!user?.legal_acceptance?.accepted_at && (
              <div className="mt-5 rounded-xl bg-yellow-50 border border-yellow-200 p-4 text-sm flex items-start gap-3" data-testid="parent-cgps-banner">
                <FileSignature className="h-5 w-5 text-yellow-700 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <strong className="text-yellow-800">{t("parentDash.cgpsBannerStrong")}</strong>
                  <span className="text-yellow-800">{t("parentDash.cgpsBanner")}</span>
                </div>
                <Button asChild size="sm" className="rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white">
                  <Link to="/legal/parents">{t("parentDash.cgpsSign")}</Link>
                </Button>
              </div>
            )}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button asChild className="rounded-full h-12 px-7 bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="parent-cta-new-match">
                <Link to="/match"><Brain className="h-4 w-4 mr-2" />{t("parentDash.ctaNewMatch")}</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full h-12 px-7 border-[#16A34A] text-[#16A34A]">
                <Link to="/educators-list">{t("parentDash.ctaBrowse")}</Link>
              </Button>
            </div>
          </div>
        </div>

        <Card className="mt-10 p-6 border-[#E5EBE7]" data-testid="parent-sessions-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl font-semibold text-[#0A2518] flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-[#16A34A]" /> {t("parentDash.sessionsTitle")}
            </h2>
          </div>
          <p className="text-sm text-[#4B5563] mb-5">{t("parentDash.sessionsHelp")}</p>
          <SessionsList mode="parent" />
        </Card>

        <MonthlyReportCard />

        <Card className="mt-10 p-6 border-[#E5EBE7]" data-testid="parent-bookings-card">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-2xl font-semibold text-[#0A2518] flex items-center gap-2">
              <CalendarCheck className="h-5 w-5 text-[#16A34A]" /> {t("parentDash.bookingsTitle")}
            </h2>
            <Button asChild size="sm" variant="ghost" className="text-[#16A34A]">
              <Link to="/educators-list">{t("parentDash.bookingsBrowse")} <ChevronRight className="h-4 w-4 ml-1" /></Link>
            </Button>
          </div>

          {bookings.length === 0 ? (
            <p className="text-sm text-[#4B5563]">{t("parentDash.bookingsEmpty")}</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="parent-bookings-grid">
              {bookings.map((b) => (
                <div key={b.id} className="rounded-xl border border-[#E5EBE7] p-4 card-hover">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display text-lg text-[#0A2518]">{b.educator_name}</div>
                      <div className="text-xs text-[#4B5563] mt-0.5">{b.subject} · {b.hours}h</div>
                    </div>
                    <Badge className={statusStyle(b.status)}>{b.status?.replace("_", " ")}</Badge>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <span className="text-[#4B5563] flex items-center gap-1"><Banknote className="h-3.5 w-3.5 text-[#16A34A]" /> {fmt(b.amount_xof)}</span>
                    <span className="text-xs text-[#4B5563]">{dfmt(b.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="mt-10 grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 p-6 border-[#E5EBE7]">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl font-semibold text-[#0A2518] flex items-center gap-2">
                <History className="h-5 w-5 text-[#16A34A]" /> {t("parentDash.historyTitle")}
              </h2>
              <Button asChild size="sm" variant="ghost" className="text-[#16A34A]">
                <Link to="/match">{t("parentDash.historyNew")} <ChevronRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            </div>

            {history.length === 0 ? (
              <div className="text-center py-12 text-[#4B5563]">
                <Brain className="h-10 w-10 text-[#16A34A]/40 mx-auto mb-3" />
                <p>{t("parentDash.historyEmpty")}</p>
                <Button asChild className="mt-4 rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white">
                  <Link to="/match">{t("parentDash.historyEmptyCta")}</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-4" data-testid="parent-match-history">
                {history.map((h) => (
                  <div key={h.id} className="rounded-xl border border-[#E5EBE7] p-5 card-hover">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <Badge className="bg-[#F0FDF4] text-[#15803D] border border-[#16A34A]/30">{h.criteria?.subject}</Badge>
                          <Badge className="bg-[#F0FDF4] text-[#15803D] border border-[#16A34A]/30">{h.criteria?.level}</Badge>
                          {h.criteria?.city && (
                            <Badge variant="outline" className="border-[#E5EBE7] text-[#0A2518]">
                              <MapPin className="h-3 w-3 mr-1" />{h.criteria.city}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-[#4B5563]">
                          {t("parentDash.historySuggested", { count: h.matches?.length || 0 })} · {dfmt(h.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <div className="space-y-6">
            <Card className="p-6 border-[#E5EBE7] ai-glow rounded-2xl">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-[#16A34A]" />
                <span className="text-xs font-semibold uppercase tracking-wide text-[#16A34A]">{t("parentDash.tipPill")}</span>
              </div>
              <h3 className="font-display text-xl font-semibold text-[#0A2518] mb-2">{t("parentDash.tipTitle")}</h3>
              <p className="text-sm text-[#4B5563]">{t("parentDash.tipText")}</p>
            </Card>

            <Card className="p-6 border-[#E5EBE7]">
              <h3 className="font-display text-xl font-semibold text-[#0A2518] mb-4 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-[#16A34A]" /> {t("parentDash.sentTitle")}
              </h3>
              {sent.length === 0 ? (
                <p className="text-sm text-[#4B5563]">{t("parentDash.sentEmpty")}</p>
              ) : (
                <div className="space-y-3" data-testid="parent-sent-requests">
                  {sent.slice(0, 5).map((s) => (
                    <div key={s.id} className="flex items-start gap-3 text-sm">
                      <Star className="h-4 w-4 text-[#16A34A] mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium text-[#0A2518]">{s.educator?.name}</div>
                        <div className="text-xs text-[#4B5563] flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {dfmt(s.created_at)} · {s.status}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
